import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { AchievementPopup, AchievementPopupData } from 'components/AchievementPopup';
import { useAuth } from 'context/AuthContext';
import { fetchChallengesWithStatus } from 'services/challenge.service';
import { invalidateCache } from 'services/cache';

interface AchievementFeedbackContextData {
  revalidateAfterAction: () => Promise<void>;
}

interface AchievementFeedbackProviderProps {
  children: ReactNode;
}

interface ChallengeStatusItem {
  id: string;
  titulo: string;
  concluida: boolean;
  icone?: string;
}

const AchievementFeedbackContext = createContext<AchievementFeedbackContextData>(
  {} as AchievementFeedbackContextData
);

function mapEarnedChallenges(challenges: ChallengeStatusItem[]): Omit<AchievementPopupData, 'position' | 'total'>[] {
  return challenges
    .filter((challenge) => challenge.concluida)
    .map((challenge) => ({
      id: challenge.id,
      titulo: challenge.titulo,
      imagem: challenge.icone
    }));
}

export function AchievementFeedbackProvider({ children }: AchievementFeedbackProviderProps) {
  const { user } = useAuth();
  const location = useLocation();
  const [queue, setQueue] = useState<AchievementPopupData[]>([]);

  const knownEarnedIdsRef = useRef<Set<string>>(new Set());
  const bootstrappedRef = useRef(false);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const autoCloseRef = useRef<NodeJS.Timeout | null>(null);

  const activeAchievement = queue.length ? queue[0] : null;
  const isElbbRoute = location.pathname.toLowerCase().includes('elbb');
  const canShowFeedback = !!user?._id && !isElbbRoute;

  const closeCurrentAchievement = useCallback(() => {
    setQueue((previousQueue) => previousQueue.slice(1));
  }, []);

  const enqueueAchievements = useCallback((newAchievements: Omit<AchievementPopupData, 'position' | 'total'>[]) => {
    if (!newAchievements.length) {
      return;
    }

    const queueBatch = newAchievements.map((achievement, index) => ({
      ...achievement,
      position: index + 1,
      total: newAchievements.length
    }));

    setQueue((previousQueue) => [...previousQueue, ...queueBatch]);
  }, []);

  const syncCurrentStatus = useCallback(async () => {
    if (!canShowFeedback || !user?._id) {
      return;
    }

    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }

    inFlightRef.current = true;

    try {
      do {
        pendingRef.current = false;

        invalidateCache(`player-challenges-${user._id}`);

        const challenges = await fetchChallengesWithStatus(user._id, { user });
        const earnedChallenges = mapEarnedChallenges(challenges);
        const earnedIds = new Set(earnedChallenges.map((challenge) => challenge.id));

        if (!bootstrappedRef.current) {
          knownEarnedIdsRef.current = earnedIds;
          bootstrappedRef.current = true;
          continue;
        }

        const newlyEarned = earnedChallenges.filter(
          (challenge) => !knownEarnedIdsRef.current.has(challenge.id)
        );

        knownEarnedIdsRef.current = earnedIds;

        enqueueAchievements(newlyEarned);
      } while (pendingRef.current);
    } catch (error) {
      // Falha silenciosa para não impactar ações do usuário
    } finally {
      inFlightRef.current = false;
    }
  }, [canShowFeedback, enqueueAchievements, user]);

  const revalidateAfterAction = useCallback(async () => {
    pendingRef.current = true;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      syncCurrentStatus();
    }, 300);
  }, [syncCurrentStatus]);

  useEffect(() => {
    setQueue([]);
    bootstrappedRef.current = false;
    pendingRef.current = false;
    inFlightRef.current = false;
    knownEarnedIdsRef.current = new Set();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (autoCloseRef.current) {
      clearTimeout(autoCloseRef.current);
      autoCloseRef.current = null;
    }

    if (canShowFeedback && user?._id) {
      syncCurrentStatus();
    }
  }, [canShowFeedback, syncCurrentStatus, user?._id]);

  useEffect(() => {
    const handleActionLogCompleted = () => {
      if (!canShowFeedback) {
        return;
      }

      revalidateAfterAction();
    };

    window.addEventListener('action-log:completed', handleActionLogCompleted);

    return () => {
      window.removeEventListener('action-log:completed', handleActionLogCompleted);
    };
  }, [canShowFeedback, revalidateAfterAction]);

  useEffect(() => {
    if (!activeAchievement) {
      return;
    }

    autoCloseRef.current = setTimeout(() => {
      closeCurrentAchievement();
    }, 5000);

    return () => {
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
      }
    };
  }, [activeAchievement, closeCurrentAchievement]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      revalidateAfterAction
    }),
    [revalidateAfterAction]
  );

  return (
    <AchievementFeedbackContext.Provider value={value}>
      {children}
      {activeAchievement && (
        <AchievementPopup
          achievement={activeAchievement}
          onClose={closeCurrentAchievement}
        />
      )}
    </AchievementFeedbackContext.Provider>
  );
}

export function useAchievementFeedback(): AchievementFeedbackContextData {
  const context = useContext(AchievementFeedbackContext);

  if (!context) {
    throw new Error('useAchievementFeedback must be used within AchievementFeedbackProvider');
  }

  return context;
}
